import Vue from 'vue'
import { normalize, schema } from 'normalizr'
import {
  RECEIVE_LOCATIONS,
  RECEIVING_LOCATIONS,
  RECEIVE_LOCATION_METADATA,
  RECEIVE_LOCATION_FAILURE,
  SAVING_LOCATION,
  SAVING_LOCATION_FAILURE,
  UPDATING_LOCATION,
  UPDATING_LOCATION_FAILURE,
  DELETE_LOCATION,
  DELETING_LOCATION,
  DELETING_LOCATION_FAILURE,
  CLEAR_LOCATION_STATE,
  RESET_LOCATION_META,
  CLEAR_STATE,
  INCREMENT_LOCATION_COUNT
} from '../mutationTypes'
import { checkStatus, getHeaders, parseJSON, buildUrl, buildUrlWithQuery } from '@/utils/api'
import handleXHRError from '@/utils/helpers/HandleXHRError'
import {
  receiveData,
  deleteData,
  increment,
  receiveMetadata,
  resetMetadata,
  clearLocalState,
  clearState
} from '@/store/modules/helpers/mutationHelpers'

const locationSchema = new schema.Entity('locations')
const locationListSchema = new schema.Array(locationSchema)

const state = {
  data: {},
  cache: {},
  count: null,
  shown: null,
  next: null,
  previous: null,
  currentPage: 1,
  saving: false,
  saveFailure: null,
  receivingData: false,
  receiveDataFailure: false,
  updating: null,
  updateFailure: null,
  deleting: false,
  deleteFailure: null
}

const actions = {
  fetchLocationsWithQuery (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_LOCATIONS, true)
    return Vue.http.get(buildUrlWithQuery('locations', query, instanceUrl), { headers: getHeaders(this.getters.getToken) })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_LOCATIONS, false)
        this.commit(RESET_LOCATION_META)
        if (!r.hasOwnProperty('results')) return this.commit(RECEIVE_LOCATION_FAILURE, 'Error fetching locations')
        const { results, next, previous, count } = r
        const { entities, result } = normalize(results, locationListSchema)
        this.commit(RECEIVE_LOCATIONS, { data: entities.locations })
        this.commit(RECEIVE_LOCATION_METADATA, { next, previous, count, result: result.length })
        return { query, count, entities }
      })
      .catch(e => {
        this.commit(RECEIVING_LOCATIONS, false)
        this.commit(RECEIVE_LOCATION_FAILURE, handleXHRError(e))
      })
  },
  fetchLocationOptions (store, query) {
    const instanceUrl = this.getters.getActiveInstance.url
    this.commit(RECEIVING_LOCATIONS, true)
    const headers = getHeaders(this.getters.getToken)
    this.commit(RECEIVING_LOCATIONS, true)
    return Vue.http({ url: buildUrl('locations', query || null, instanceUrl), headers, method: 'options' })
      .then(checkStatus)
      .then(parseJSON)
      .then(r => {
        this.commit(RECEIVING_LOCATIONS, false)
        if (r.hasOwnProperty('choices')) {
          const { choices } = r
          const { entities, result } = normalize(choices, locationListSchema)
          if (!result) return this.commit(RECEIVE_LOCATION_FAILURE, 'Error normalizing response')
          this.commit(RECEIVE_LOCATIONS, { next: '', data: entities.locations })
          this.commit(RECEIVING_LOCATIONS, false)
          return r
        }
      })
      .catch(e => {
        this.commit(RECEIVING_LOCATIONS, false)
        this.commit(RECEIVE_LOCATION_FAILURE, handleXHRError(e))
      })
  }
}

const getters = {
  getLocations: (state) => {
    return state.data ? Object.values(state.data).map(v => Object.assign({}, v)) : []
  },
  getAllLocations: (state) => {
    return state.cache ? Object.values(state.cache).map(v => Object.assign({}, v)) : []
  },
  getLocationMetadata: (state) => {
    const { shown, count } = state
    if (!shown) return { count: 0, shown: 0 }
    return { count, shown }
  },
  getLocationById: (state) => (id) => {
    if (state.data && state.data[id]) {
      return state.data[id]
    } else if (state.cache && state.cache[id]) {
      return state.cache[id]
    } else {
      return {}
    }
  },
  getDefaultLocation: (state) => {
    return state.cache ? Object.values(state.cache).find(v => v.active) : {}
  },
  getLocationFetchStatus: (state) => {
    return state.receivingData
  },
  getLocationSaveStatus: (state) => {
    return state.saving
  },
  getLocationUpdateStatus: (state) => {
    return state.updating
  },
  getLocationDeleteStatus: (state) => {
    return state.deleting
  }
}

const mutations = {
  [RECEIVE_LOCATIONS] (state, results) {
    receiveData(state, results)
  },
  [RECEIVING_LOCATIONS] (state, payload) {
    state.receivingData = payload
  },
  [RECEIVE_LOCATION_FAILURE] (state, error) {
    state.receiveDataFailure = error
  },
  [SAVING_LOCATION] (state, payload) {
    state.saving = payload
  },
  [SAVING_LOCATION_FAILURE] (state, payload) {
    state.saveFailure = payload
  },
  [UPDATING_LOCATION] (state, payload) {
    state.updating = payload
  },
  [UPDATING_LOCATION_FAILURE] (state, payload) {
    const { id, err } = payload
    state.updateFailure = { id, err }
  },
  [DELETE_LOCATION] (state, payload) {
    deleteData(state, payload)
  },
  [DELETING_LOCATION] (state, payload) {
    state.deleting = payload
  },
  [DELETING_LOCATION_FAILURE] (state, payload) {
    const { id, err } = payload
    state.deleteFailure = { id, err }
  },
  [INCREMENT_LOCATION_COUNT] (state, payload) {
    increment(state, payload)
  },
  [RECEIVE_LOCATION_METADATA] (state, payload) {
    receiveMetadata(state, payload)
  },
  [CLEAR_LOCATION_STATE] (state, payload) {
    clearLocalState(state, payload)
  },
  [RESET_LOCATION_META] (state) {
    resetMetadata(state)
  },
  [CLEAR_STATE] (state) {
    clearState(state)
  }
}

export default {
  state,
  actions,
  getters,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/modules/location.js